import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, ListPlus, Pencil, Play, Trash2 } from "lucide-react";

import { eventService, type EventTask, type SchoolEvent } from "../../services/eventService";
import { useAuthStore } from "../../stores/authStore";
import { todayIso } from "../../utils/dates";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { TaskForm } from "./EventForms";
import { PRIORITIES, clock, day, isOpenTask, labelFrom, taskConfig, taskStatus } from "./eventsLogic";

/** Who is doing what for every event, across the calendar. */
export function TasksPanel({ tasks, events, isLoading, isError, onRetry, onChanged, initialView }: {
  tasks?: EventTask[];
  events: SchoolEvent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
}) {
  const today = todayIso();
  const me = useAuthStore((state) => state.user?.id);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<EventTask | "new" | null>(null);
  const [finishing, setFinishing] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const move = useMutation({ mutationFn: ({ id, status }: { id: number; status: string }) => eventService.updateTask({ id, data: { status } }), onSuccess: (row) => done(`${row.title}: ${taskStatus(row, today).label.toLowerCase()}.`) });
  const remove = useMutation({ mutationFn: eventService.deleteTask, onSuccess: () => done("The task was removed.", true) });

  const columns: RegisterColumn<EventTask>[] = [
    { header: "Due", cell: (row) => (row.due_date ? `${day(row.due_date)}${row.due_time ? ` ${clock(row.due_time)}` : ""}` : "—"), exportValue: (row) => day(row.due_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Task", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[12rem]" },
    { header: "Event", cell: (row) => `${row.event_name} · ${day(row.event_start_date)}`, exportValue: (row) => row.event_name, className: "text-gray-700" },
    { header: "Given to", cell: (row) => row.assigned_to_name, exportValue: (row) => row.assigned_to_name, className: "text-gray-700" },
    { header: "Priority", cell: (row) => <span className={row.priority === "urgent" || row.priority === "high" ? "font-medium text-red-700" : "text-gray-700"}>{labelFrom(PRIORITIES, row.priority)}</span>, exportValue: (row) => labelFrom(PRIORITIES, row.priority) },
    { header: "Status", cell: (row) => <StatusDot {...taskStatus(row, today)} />, exportValue: (row) => taskStatus(row, today).label },
    { header: "How it was done", cell: (row) => row.completion_notes ?? "", exportValue: (row) => row.completion_notes ?? "", exportOnly: true },
  ];
  const task = viewing !== null ? tasks?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Planning tasks"
        description="Who is doing what for each event. Add tasks here or from an event's panel."
        actions={
          <button type="button" disabled={!events.some((row) => row.status !== "cancelled" && row.status !== "completed")} onClick={() => { setFinishing(false); setEditing("new"); }} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <ListPlus className="mr-1.5 h-3.5 w-3.5" /> New task
          </button>
        }
        rows={tasks}
        config={taskConfig(today, me)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          move.reset();
          remove.reset();
          setViewing(row.id);
        }}
        noun={["task", "tasks"]}
        exportName={{ file: "event-tasks", title: "Event Planning Tasks" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No planning tasks yet."
      />

      {task && (
        <DetailsModal
          title={task.title}
          headline={{ label: task.event_name, value: task.due_date ? `Due ${day(task.due_date)}` : "No due date", note: `Given to ${task.assigned_to_name}` }}
          status={taskStatus(task, today)}
          facts={[
            ["Priority", labelFrom(PRIORITIES, task.priority)],
            ["Event date", day(task.event_start_date)],
            ["Added by", task.created_by_name],
            ["Done on", task.completed_date ? new Date(task.completed_date).toLocaleString() : undefined],
          ]}
          sections={[
            { title: "Details", body: task.description },
            { title: "How it was done", body: task.completion_notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(task.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                <Trash2 className="h-4 w-4" /> Remove
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" onClick={() => { setFinishing(false); setEditing(task); }} className="btn btn-outline gap-1.5"><Pencil className="h-4 w-4" /> Change</button>
              {task.status === "pending" && (
                <button type="button" disabled={move.isPending} onClick={() => move.mutate({ id: task.id, status: "in_progress" })} className="btn btn-outline gap-1.5"><Play className="h-4 w-4" /> Start it</button>
              )}
              {isOpenTask(task) && (
                <button type="button" onClick={() => { setFinishing(true); setEditing(task); }} className="btn btn-primary gap-1.5" title="Mark it done, with a note on how it went"><CheckCircle2 className="h-4 w-4" /> Mark done</button>
              )}
            </>
          }
        >
          {[move, remove].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}
        </DetailsModal>
      )}

      {editing && (
        <TaskForm
          task={editing === "new" ? undefined : editing}
          initialStatus={finishing ? "completed" : undefined}
          events={events}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            setEditing(null);
            done(editing === "new" ? `${saved.title} added.` : `${saved.title} updated.`);
          }}
        />
      )}
    </div>
  );
}
